import React from "react";
import { useSelector } from "react-redux";
import { State } from "../../../app/store";
import SubpageNavBarButton from "./SubpageNavBarButton";
import LoginNavButton from "./LoginNavButton";
import EASkeletonLoader from "../../../app/components/shared/EASkeletonLoader";
import { Link } from "react-router-dom";
import { GDANSK_SALE } from "../../../app/routes";
import useFacility from "../../../utils/hooks/useFacility";
import useCustomOffer from "../../../app/components/CustomOffer/hooks/useCustomOffer";
import B2BNavButton from "./B2BNavButton";
import PriorityNaviLinks, { PriorityNavEntry } from "./PriorityNaviLinks";

interface IProps {
  onClick?: () => void;
  /** Poziomy pasek: linki, które się nie mieszczą, chowają się pod „Więcej”. */
  collapseOverflow?: boolean;
}

const NaviLinks: React.FC<IProps> = ({ onClick = () => {}, collapseOverflow = false }) => {
  const saleConfiguration = useSelector((state: State) => state.config.configuration);
  const facilityIds = useFacility();
  const { customOfferLinks } = useCustomOffer();
  const userData = useSelector((state: State) => state.auth.userData);

  const getPatchFromLink = (link: string) => {
    const url = new URL(link, window.location.origin);
    const linkPath = url.pathname + url.search + url.hash;

    // Dla użytkowników B2B zachowujemy parametr id i B2BSESSION z aktualnego URL
    if (Boolean(userData.b2bType)) {
      const currentParams = new URLSearchParams(window.location.search);
      const currentId = currentParams.get("id");
      const b2bSession = currentParams.get("B2BSESSION");

      // Dodajemy parametry do URL linku
      const linkUrl = new URL(link, window.location.origin);
      const linkParams = new URLSearchParams(linkUrl.search);

      if (currentId) {
        linkParams.set("id", currentId);
      } else if (facilityIds) {
        linkParams.set("id", facilityIds);
      }

      if (b2bSession) {
        linkParams.set("B2BSESSION", b2bSession);
      }

      const newLinkPath = linkUrl.pathname + (linkParams.toString() ? `?${linkParams.toString()}` : "") + linkUrl.hash;
      return newLinkPath;
    }

    return linkPath;
  };

  const renderLinks = (entries: PriorityNavEntry[], pinned: React.ReactNode) => (
    <div className={`navi-links${collapseOverflow ? " navi-links--priority" : ""}`}>
      {collapseOverflow ? (
        <PriorityNaviLinks entries={entries} />
      ) : (
        entries.map((entry) => <React.Fragment key={entry.key}>{entry.node}</React.Fragment>)
      )}
      {pinned}
    </div>
  );

  if (!userData.b2bType && !saleConfiguration.componentList) {
    return <EASkeletonLoader height="500px" />;
  }

  if (!userData.b2bType) {
    const entries: PriorityNavEntry[] = [
      ...(saleConfiguration.componentList ?? []).map((item, index) => ({
        key: `component-${item.id}-${index}`,
        node: <SubpageNavBarButton item={item} onClick={onClick} />,
      })),
      ...customOfferLinks.map((item, index) => ({
        key: `custom-${item.id}-${index}`,
        node: <SubpageNavBarButton item={item} isCustomOffer onClick={onClick} />,
      })),
    ];

    if (facilityIds === "1235" || facilityIds === "1236" || facilityIds === "1237" || facilityIds === "1238") {
      entries.push({
        key: "gdansk",
        node: (
          <Link onClick={onClick} className="navi-link" to={GDANSK_SALE}>
            Bilety/Karnety
          </Link>
        ),
      });
    }

    return renderLinks(entries, saleConfiguration.allowToMakeAccount ? <LoginNavButton onClick={onClick} /> : null);
  }

  const b2bEntries: PriorityNavEntry[] = [
    ...userData.b2bLinks.map((item, index) => ({
      key: `b2b-${index}`,
      node: (
        <Link onClick={onClick} className="navi-link" to={getPatchFromLink(item.link)}>
          {item.name}
        </Link>
      ),
    })),
    ...(saleConfiguration.componentList ?? []).map((item, index) => ({
      key: `b2b-component-${item.id}-${index}`,
      node: (
        <SubpageNavBarButton item={item} onClick={onClick} linkTransform={getPatchFromLink} />
      ),
    })),
  ];

  return renderLinks(b2bEntries, <B2BNavButton />);
};

export default NaviLinks;
